import { CheckCircleFilled, CloseCircleFilled, MinusCircleFilled } from "@ant-design/icons";

import { ERROR_500, GRAY_500, PRIMARY_600, SUCCESS_500, WARNING_500 } from "../../../constant/colors";
import { AGENTIC_RUN_STATUS, RUN_FINAL_OUTCOME } from "../../../constant/agenticPlatform";

/**
 * Icon per audit-step result (`passed` / `failed` / `skipped`). Single source of
 * truth for WHICH glyph represents each result across the agentic UI — the run
 * modal's step chips (`STEP_RESULT_VISUAL`) and the HITL summary dots
 * (`SUMMARY_RESULT_VISUAL`) build their own colors/shape around these but must
 * agree on the icon, so a result never shows two different glyphs in two places.
 */
export const RESULT_ICON = Object.freeze({
  passed: CheckCircleFilled,
  failed: CloseCircleFilled,
  skipped: MinusCircleFilled
});

/**
 * Single source of truth for run-status visuals — shared by the patient-page
 * `<AgenticRunsButton>` (label text color) and the `<AgenticRunsModal>`
 * (`<Tag>` color). Keys mirror the platform's `AgenticRunStatus` enum
 * (`agentic_workflow_runs.status`) so a new bucket added on the platform is
 * surfaced consistently across both surfaces by editing this one map.
 *
 * Each entry carries:
 *   - `labelKey` / `fallback`  — i18n label (button + modal use the SAME copy)
 *   - `antdTagColor`           — Antd `<Tag>` color preset for the modal
 *   - `hex`                    — explicit hex for the button's label text
 */
export const RUN_STATUS_META = Object.freeze({
  running: {
    labelKey: "agentic_runs.status.running",
    fallback: "Agent running",
    antdTagColor: "processing",
    hex: PRIMARY_600
  },
  waiting_hitl: {
    labelKey: "agentic_runs.status.waiting_hitl",
    fallback: "Awaiting navigator",
    // In-flight / action-needed: the workflow is alive and parked on a
    // human-in-the-loop task, not a quiet terminal state — so it uses the
    // warning palette to read as "waiting on a person".
    antdTagColor: "warning",
    hex: WARNING_500
  },
  sent: {
    labelKey: "agentic_runs.status.sent",
    fallback: "Consent sent",
    antdTagColor: "success",
    hex: SUCCESS_500
  },
  declined: {
    labelKey: "agentic_runs.status.declined",
    fallback: "Declined",
    // A navigator declining the consent send is a legitimate decision, not a
    // failure — so it reads as a neutral/gray terminal state (like
    // not_eligible / oop_met), NOT the red error palette reserved for genuine
    // crashes (`failed`).
    antdTagColor: "default",
    hex: GRAY_500
  },
  not_eligible: {
    labelKey: "agentic_runs.status.not_eligible",
    fallback: "Not eligible",
    antdTagColor: "default",
    hex: GRAY_500
  },
  oop_met: {
    labelKey: "agentic_runs.status.oop_met",
    fallback: "OOP met",
    antdTagColor: "default",
    hex: GRAY_500
  },
  no_active_medications: {
    labelKey: "agentic_runs.status.no_active_medications",
    fallback: "No active meds",
    antdTagColor: "default",
    hex: GRAY_500
  },
  has_active_consent: {
    labelKey: "agentic_runs.status.has_active_consent",
    fallback: "Has consent",
    antdTagColor: "success",
    hex: SUCCESS_500
  },
  failed: {
    labelKey: "agentic_runs.status.failed",
    fallback: "Failed",
    antdTagColor: "error",
    hex: ERROR_500
  }
});

/**
 * Friendly labels for the supported hypercare reasons (HypercareReason on the
 * platform). Single source of truth — shared by the run modal and the HITL task
 * modal, so adding a reason on the platform is surfaced everywhere by editing
 * this one map. Unknown codes fall through to the raw code via
 * {@link hypercareReasonLabel}.
 */
export const HYPERCARE_REASON_LABELS = Object.freeze({
  COMMERCIAL_NEW_MEDICATION: "Commercial · new medication",
  COMMERCIAL_DONE_ESTABLISHED: "Commercial · done / established",
  COMMERCIAL_ACTIVE_TREATMENT: "Commercial · active treatment",
  MEDICARE_NEW_MEDICATION: "Medicare · new medication",
  MEDICARE_DONE_ESTABLISHED: "Medicare · done / established"
});

/** Label for a hypercare reason code; unknown codes return the raw code. */
export const hypercareReasonLabel = (code) => HYPERCARE_REASON_LABELS[code] || code;

/**
 * The display bucket for a TERMINAL run, derived from `run.finalOutcome` (the
 * verdict) — `run.status` is pure lifecycle (succeeded/failed) and never
 * carries it. Verdicts with no bucket fall back to the generic label
 * (failure keeps the failed bucket via `getRunStatusMeta`).
 */
const FINAL_OUTCOME_META_KEY = Object.freeze({
  [RUN_FINAL_OUTCOME.CONSENT_SENT_BY_NAVIGATOR]: "sent",
  [RUN_FINAL_OUTCOME.REVIEW_DECLINED]: "declined",
  [RUN_FINAL_OUTCOME.NOT_ELIGIBLE]: "not_eligible",
  [RUN_FINAL_OUTCOME.OOP_MET]: "oop_met",
  [RUN_FINAL_OUTCOME.NO_ACTIVE_MEDICATIONS]: "no_active_medications",
  [RUN_FINAL_OUTCOME.PATIENT_HAS_ACTIVE_CONSENT]: "has_active_consent",
  [RUN_FINAL_OUTCOME.FAILURE]: "failed"
});

/**
 * Meta lookup for a RUN (not a bare status string). `run.status` is pure
 * lifecycle: alive runs resolve directly; terminal runs resolve through
 * `finalOutcome` via `FINAL_OUTCOME_META_KEY`. A failed run with an unmapped
 * verdict still reads "Failed"; a succeeded run with an unmapped code falls
 * back to the generic label.
 *
 * The run passed here is the JOURNEY ROOT (`rx-conductor`), so its status is
 * deliberately COARSE — `running` / `waiting_hitl` (only ever `consent_decide`,
 * the one pause the conductor itself owns) / `succeeded` / `failed`. Pauses
 * owned by child workflows (`emr_screenshots` on screenshot-collection,
 * `emr_fill` on gather-emr) live on THEIR rows and never surface here: a
 * journey is a tree, and once it can fan out to several enrollments at once
 * there is no single correct "which pause" label for the root. Per-phase
 * detail belongs in the modal's step timeline (merged across the tree), and
 * per-pause work belongs in the Tasks hub / EMR Tasks tab.
 */
export const getRunStatusMeta = (run) => {
  if (!run?.status) {
    return null;
  }
  if (run.status === AGENTIC_RUN_STATUS.SUCCEEDED || run.status === AGENTIC_RUN_STATUS.FAILED) {
    const bucket = FINAL_OUTCOME_META_KEY[run.finalOutcome];
    if (bucket && RUN_STATUS_META[bucket]) {
      return RUN_STATUS_META[bucket];
    }
    return run.status === AGENTIC_RUN_STATUS.FAILED ? RUN_STATUS_META.failed : null;
  }
  return RUN_STATUS_META[run.status] ?? null;
};
